import { Box, Button, Stack, Typography } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { GLItemKind, GLItemStatus, GLItemStatuses } from '../../../DTO/GLItemDTO'
import { GLItemsDTO, SalesChartMode, SalesChartSeries } from '../../../DTO/GLItemsDTO'
import mainservice from '../../../services/MainService'
import './SalesPotentialChart.scss'

const euro = new Intl.NumberFormat('de-DE', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 0,
})

function marketingStatusOptions(): Array<{ value: string, title: string }> {
  return (GLItemStatuses[GLItemKind.marketing] || [])
    .filter(s => !(s as { hideInTabs?: boolean }).hideInTabs)
    .map(s => ({
      value: s.status === undefined ? 'all' : String(s.status),
      title: s.name,
    }))
}

function QuarterAxisTick(props: {
  x?: number
  y?: number
  payload?: { value: string }
  index?: number
  points?: Array<{ quarterKey: string, quarter: string, year: number, label: string }>
}) {
  const { x = 0, y = 0, index = 0, payload, points = [] } = props
  const point = points.find(p => p.quarterKey === payload?.value) || points[index]
  if (!point) {
    return null
  }
  const showYear = point.quarter === 'Q1' || index === 0 || (index > 0 && points[index - 1]?.year !== point.year)
  return (
    <g transform={`translate(${x},${y})`}>
      <text dy={14} textAnchor="middle" fontSize={11} fill="#444">{point.quarter}</text>
      {showYear && (
        <text dy={30} textAnchor="middle" fontSize={11} fontWeight={600} fill="#222">{point.year}</text>
      )}
    </g>
  )
}

function SalesChartTooltip(props: {
  active?: boolean
  payload?: Array<{ dataKey?: string, value?: number, payload?: { year: number, quarter: string } }>
  seriesByKey: Map<string, SalesChartSeries>
  mode: SalesChartMode
}) {
  if (!props.active || !props.payload || props.payload.length === 0) {
    return null
  }
  const entry = props.payload.find(p => (p.value || 0) > 0) || props.payload[0]
  const series = props.seriesByKey.get(String(entry.dataKey || ''))
  const point = entry.payload
  if (!series || !point) {
    return null
  }
  const name = props.mode === 'company'
    ? series.customerName
    : `${series.customerName} – ${series.headline}`
  return (
    <Box
      sx={{
        backgroundColor: '#fff',
        border: '1px solid #ccc',
        borderRadius: 1,
        padding: 1,
        boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
      }}
    >
      <Typography variant="body2" sx={{ color: '#666' }}>
        {point.year} {point.quarter}
      </Typography>
      <Typography variant="body2">{name}</Typography>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {euro.format(entry.value || 0)}
      </Typography>
    </Box>
  )
}

export default function SalesPotentialChart(props: {
  mode: SalesChartMode
  variant: 'teaser' | 'full'
  kind?: GLItemKind
  initialStatus?: GLItemStatus
  onOpenFull?: () => void
}) {
  const kind = props.kind ?? GLItemKind.marketing
  const [dto] = useState(() => new GLItemsDTO({ kind }))
  const [loading, setLoading] = useState(true)
  const [status, setStatus] = useState<GLItemStatus | undefined>(props.initialStatus)
  const [filterId, setFilterId] = useState('')
  const [tick, setTick] = useState(0)

  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoading(true)
      await dto.getChartFromServer({ kind })
      if (!cancelled) {
        setLoading(false)
        setTick(t => t + 1)
      }
    }
    load()
    return () => { cancelled = true }
  }, [dto, kind])

  useEffect(() => {
    setFilterId('')
  }, [props.mode])

  useEffect(() => {
    setStatus(props.initialStatus)
  }, [props.initialStatus])

  const stack = useMemo(() => {
    void tick
    return dto.buildStackedChart(props.mode, status, filterId || undefined)
  }, [dto, props.mode, status, filterId, tick])

  const seriesByKey = useMemo(() => {
    const map = new Map<string, SalesChartSeries>()
    stack.series.forEach(s => map.set(s.key, s))
    return map
  }, [stack.series])

  const hasBars = stack.series.length > 0
  const height = props.variant === 'teaser' ? 260 : 420

  function openFull() {
    if (props.onOpenFull) {
      props.onOpenFull()
      return
    }
    mainservice.navTo([
      ['view', 'spMarketingChart'],
      ['status', 'all'],
      ['chartMode', props.mode],
    ])
  }

  function changeStatus(raw: string) {
    const next = raw === 'all' ? undefined : parseInt(raw, 10)
    setStatus(Number.isNaN(next as number) ? undefined : next)
    setFilterId('')
    if (props.variant === 'full') {
      mainservice.nav.setVal('status', raw)
    }
  }

  function jumpToProject(series: SalesChartSeries) {
    if (props.mode !== 'project') {
      return
    }
    mainservice.navTo([
      ['view', 'spMarketing'],
      ['status', String(series.status)],
      ['id', series.itemId],
    ])
  }

  const statusOptions = marketingStatusOptions()
  const entityLabel = props.mode === 'company' ? 'Firma' : 'Projekt'

  return (
    <div className={`SalesPotentialChart SalesPotentialChart-${props.variant}`}>
      <div className="SalesPotentialChart-header">
        <Typography
          variant="h5"
          component="h3"
          className="SalesPotentialChart-title"
          onClick={props.variant === 'teaser' ? openFull : undefined}
        >
          Gewichtetes Potenzial
        </Typography>
        {props.variant === 'teaser' && (
          <Button size="small" onClick={openFull} sx={{ textTransform: 'none' }}>Öffnen</Button>
        )}
      </div>

      {props.variant === 'full' && (
        <div className="SalesPotentialChart-filters">
          <div className="SalesPotentialChart-filter">
            <label htmlFor="sales-chart-status">Status</label>
            <select
              id="sales-chart-status"
              value={status === undefined ? 'all' : String(status)}
              onChange={e => changeStatus(e.target.value)}
            >
              {statusOptions.map(o => (
                <option key={o.value} value={o.value}>{o.title}</option>
              ))}
            </select>
          </div>
          <div className="SalesPotentialChart-filter">
            <label htmlFor="sales-chart-entity">{entityLabel}</label>
            <select
              id="sales-chart-entity"
              value={filterId}
              onChange={e => setFilterId(e.target.value)}
            >
              <option value="">Alle</option>
              {stack.filterOptions.map(o => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {loading && <div className="SalesPotentialChart-loading">Lade…</div>}
      {!loading && !hasBars && (
        <div className="SalesPotentialChart-empty">
          Keine Einträge mit Quartal und gewichtetem Potenzial
        </div>
      )}

      {!loading && hasBars && (
        <div className="SalesPotentialChart-body">
          <ResponsiveContainer width="100%" height={height}>
            <BarChart data={stack.points} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="quarterKey"
                interval={0}
                height={40}
                tick={<QuarterAxisTick points={stack.points} />}
              />
              <YAxis
                tickFormatter={(v: number) => euro.format(v)}
                width={88}
              />
              <Tooltip
                shared={false}
                cursor={{ fill: 'rgba(0,0,0,0.04)' }}
                content={(tipProps) => (
                  <SalesChartTooltip
                    active={tipProps.active}
                    payload={tipProps.payload as any}
                    seriesByKey={seriesByKey}
                    mode={props.mode}
                  />
                )}
              />
              {stack.series.map(s => (
                <Bar
                  key={s.key}
                  dataKey={s.key}
                  name={s.label}
                  stackId="potential"
                  fill={s.color}
                  maxBarSize={48}
                  cursor={props.mode === 'project' && props.variant === 'full' ? 'pointer' : 'default'}
                  onClick={() => {
                    if (props.variant === 'full') {
                      jumpToProject(s)
                    }
                  }}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {props.variant === 'full' && props.mode === 'project' && hasBars && (
        <div className="SalesPotentialChart-hint">
          Klick auf ein Segment öffnet die zugehörige Vertriebskarte.
        </div>
      )}
    </div>
  )
}

export function SalesPotentialModeToggle(props: {
  mode: SalesChartMode
  onChange: (mode: SalesChartMode) => void
}) {
  return (
    <Stack direction="row" spacing={1} sx={{ marginBottom: 2 }}>
      <Button
        variant={props.mode === 'company' ? 'contained' : 'outlined'}
        onClick={() => props.onChange('company')}
        sx={{ textTransform: 'none' }}
      >
        Firmen
      </Button>
      <Button
        variant={props.mode === 'project' ? 'contained' : 'outlined'}
        onClick={() => props.onChange('project')}
        sx={{ textTransform: 'none' }}
      >
        Projekte
      </Button>
    </Stack>
  )
}
